import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import AvatarWithName from '@components/avatar-with-name/AvatarWithName';
import { FEATURE_FLAGS } from '@constants/feature';
import { AccountType, OrgStatus } from '@constants/generic';
import {
  INDIVIDUAL_PROFILE_ITEMS,
  ORG_PROFILE_SECTIONS
} from '@constants/sideMenu';
// import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { MixpanelEventV2, sendMixpanelEventV2 } from '@core/analytics/mixpanel';
import useCheckPermission from '@hooks/checkPermission';
import ProfileOptions from '@layouts/side-nav-bar/ProfileOptions';
import { getPhoneNumberWith91 } from '@utils/generic';

import { ProfileSettingsProps } from './types';

import theme from '../../../themes/colors';

const TRIAL_ORG_ALLOWED_IDS = new Set([
  'org-dashboard',
  'report',
  'expence-tracker',
  'lead-management',
  'team-management',
  'subscriptions',
  'org-settings'
]);

const TRIAL_INDIVIDUAL_ALLOWED_IDS = new Set([
  'report',
  'org-settings',
  'subscriptions'
]);

const NAV_ITEM_EVENT_MAP: Record<string, MixpanelEventV2> = {
  'org-dashboard': MixpanelEventV2.ORG_DASHBOARD_CLICKED,
  'org-settings': MixpanelEventV2.CONFIGURATIONS_CLICKED,
  report: MixpanelEventV2.REPORTS_CLICKED,
  subscriptions: MixpanelEventV2.PLAN_AND_BILLING_CLICKED
};

const filterTrialItems = (
  sections: any[][],
  allowedIds: Set<string>
): any[][] =>
  sections
    .map((section) => section.filter((item: any) => allowedIds.has(item.id)))
    .filter((section) => section.length > 0);

const ProfileSettings = (props: ProfileSettingsProps) => {
  const { orgList, subscriptionData, accountMethods } = props;
  const { checkPermission } = useCheckPermission();
  const { currentOrg, currentUser, accountType } = useSelector(
    (state: any) => state.user
  );
  const { orgFeatureFlags } = useSelector((state: any) => state.user);
  const isTrialMode = useSelector((state: any) => state.trial?.isTrialMode);

  const isOrg = accountType.type === AccountType.ORGANIZATION;
  const fullMenuItems = isOrg ? ORG_PROFILE_SECTIONS : INDIVIDUAL_PROFILE_ITEMS;

  const menuItems = isTrialMode
    ? filterTrialItems(
        fullMenuItems,
        isOrg ? TRIAL_ORG_ALLOWED_IDS : TRIAL_INDIVIDUAL_ALLOWED_IDS
      )
    : fullMenuItems;

  const isExcludedSideNavItem = (id: string) => {
    if (id === 'subscriptions')
      return (
        currentOrg?.status === OrgStatus.VERIFICATION_PENDING ||
        currentOrg?.status === OrgStatus.VERIFICATION_SUBMITTED
      );

    if (id === 'docs')
      return !(
        orgFeatureFlags &&
        orgFeatureFlags.includes(FEATURE_FLAGS.API_INTEGRATIONS)
      );

    if (id === 'org-dashboard') return !!currentOrg?.has_multiple_currencies;

    return false;
  };

  // const profileItemClick = (label: string) => {
  //   sendMixpanelEvent(Event.SIDEBAR_TAB_CLICKED, {
  //     section: label
  //   });
  // };

  const onNavItemClick = (id: string) => {
    const event = NAV_ITEM_EVENT_MAP[id];
    if (event) sendMixpanelEventV2(event);
  };

  // Helper to filter and exclude items in a section
  const getPermittedSectionItems = (section: any[]) =>
    section.filter(
      (navItem: any) =>
        (!navItem?.permissions ||
          navItem?.permissions.some((permission: any) =>
            checkPermission([permission])
          )) &&
        (!navItem?.featureFlag ||
          orgFeatureFlags?.includes(navItem?.featureFlag)) &&
        !isExcludedSideNavItem(navItem.id)
    );

  const renderOrgProfileSections = () =>
    menuItems.map((section, sectionIdx) => {
      const permittedItems = getPermittedSectionItems(section);
      if (permittedItems.length === 0) return null;
      const sectionKey =
        permittedItems[0]?.id ||
        JSON.stringify(permittedItems.map((i) => i.id));
      let showDivider = false;
      if (sectionIdx < menuItems.length - 1) {
        const nextSection = menuItems[sectionIdx + 1];
        const nextPermitted = getPermittedSectionItems(nextSection);
        showDivider = nextPermitted.length > 0;
      }
      return (
        <div key={sectionKey}>
          {permittedItems.map((item) => {
            const { id, Icon: IconComponent, route, label } = item;
            return (
              <Link
                key={id}
                to={route}
                className="mx-1 flex items-center gap-3 rounded-md px-4 py-2.5 text-sm text-cofeeNeutral10 hover:bg-cofeeGreyBlue75"
                onClick={() => onNavItemClick(id)}
              >
                {IconComponent && (
                  <IconComponent className="size-5 !text-cofeeNeutral10" />
                )}
                {label}
              </Link>
            );
          })}
          {showDivider && <div className="h-px w-full bg-cofeeNeutral5" />}
        </div>
      );
    });

  return (
    <div className="absolute right-2.5 top-16 z-20 w-74 min-w-48 cursor-default overflow-hidden rounded-2xl bg-cofeeNeutral1 text-sm font-normal shadow-md">
      <div className="border-b border-b-cofeeNeutral5 p-4">
        <AvatarWithName
          name={currentUser.name}
          subText={getPhoneNumberWith91(currentUser.mobile)}
          bgColor={theme.cofeeGreen1}
          wrapperClass="!gap-2 !items-start"
          avatarClass="h-[50px] w-[50px] min-w-[50px] text-xl text-white"
          nameClass="!text-sm text-cofeeNeutral10 font-semibold break-all"
          subTextClass="text-cofeeNeutral8 mt-2"
          textWrapperClass="!items-start !text-left"
        />
      </div>
      <div className="my-1">{renderOrgProfileSections()}</div>
      <ProfileOptions
        orgList={orgList}
        accountMethods={accountMethods}
        subscriptionData={subscriptionData}
        labelStyle="font-medium text-cofeeNeutral10"
        isProfileList={false}
      />
    </div>
  );
};

export default ProfileSettings;
